// Core
import { useMemo, useEffect, useState } from 'react';
// Global UI component
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
// Vendors
import { useFormContext } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
// Global types
import { IInvoice } from '@/types/invoice';
import { getInvoices } from '@/actions/invoice/invoice-queries';
import { getLocalYear } from '@/lib/date-formatter';

const InvoiceNumberGenerator = () => {
  const t = useTranslations('CreateInvoicePopup');

  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const { setValue, watch } = useFormContext();
  const serviceDateFrom = watch('serviceDate.from');
  // Extract year from local date to avoid timezone issues
  // This ensures dates like "2026-01-01" stay as 2026, not 2025 due to UTC conversion
  const serviceYear = serviceDateFrom
    ? getLocalYear(serviceDateFrom)
    : getLocalYear(new Date());

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const data = await getInvoices();

        const formattedInvoices = data.invoices.map((invoice) => ({
          ...invoice,
          items: invoice.items.map((item) => ({
            ...item,
            price: +item.price,
            qty: +item.qty,
            tax: +item.tax,
          })),
        }));

        setInvoices(Array.isArray(formattedInvoices) ? formattedInvoices : []);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
      }
    };

    fetchInvoices();
  }, []);

  const nextInvoiceNumber = useMemo(() => {
    const prefix = `${serviceYear}-`;

    // Filter invoices by invoice number prefix (not serviceFrom year)
    // This matches the server-side logic
    const serviceYearInvoices = invoices.filter((invoice) =>
      invoice.invoiceNumber.startsWith(prefix)
    );

    const lastInvoiceNumber = serviceYearInvoices.length
      ? Math.max(
          ...serviceYearInvoices.map((invoice) => {
            const parts = invoice.invoiceNumber.split('-');
            const num = parseInt(parts[1], 10);
            return isNaN(num) ? 0 : num;
          })
        )
      : 0;

    return `${serviceYear}-${lastInvoiceNumber + 1}`;
  }, [invoices, serviceYear]);

  useEffect(() => {
    setValue('invoiceNumber', nextInvoiceNumber);
  }, [nextInvoiceNumber, setValue]);

  const invoiceNumber = watch('invoiceNumber');

  return (
    <FormField
      name="invoiceNumber"
      render={({ field }) => (
        <FormItem className="flex flex-col w-full sm:w-1/2">
          <FormLabel>{t('invoiceNumber')}</FormLabel>

          <FormControl>
            <Input {...field} value={invoiceNumber} />
          </FormControl>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default InvoiceNumberGenerator;
